/**
 * Planbord Page (Epic C-3 — Planbord & indelen, Story C-3.3, UX-C1;
 * uitgebreid met conflict-signalering in Epic C-4, Story C-4.3, UX-C5)
 *
 * Week-navigatie + grid + bezettingsstrook + legenda + states. Route
 * `/planning/planbord` (nav-integratie volgt in Story C-5.2, zelfde
 * incrementele opleverpatroon als `/planning/taken`/`/planning/kwalificaties`).
 *
 * Scope server-side (planningService.getBoard, AD-16): leidinggevende ziet
 * eigen team, HR/admin org-breed met optioneel afdeling-filter — de
 * afdeling-selector hieronder is daarom alleen zichtbaar voor HR/admin
 * (UX-C1 "afdeling-selector, alleen HR").
 *
 * Story C-4.3: conflict-banner + rechts-Sheet-conflictpaneel, bewust GEEN
 * eigen tab/route (UX-C5). "Indeling verwijderen" is een echte DELETE
 * (AD-11); de undo-toast is een NIEUWE `upsertAssignment`-aanroep, geen
 * aparte undo-infrastructuur. "Aanpassen in planbord" navigeert (indien
 * nodig) naar de juiste week en scrollt/highlight de cel.
 */
import { useEffect, useState } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../../components/Toast';
import { useDepartments } from '../settings/useSettings';
import { usePlanningBoard, useUpsertAssignment, useDeleteAssignment } from './hooks/usePlanningBoard';
import { usePlanningTasks } from './hooks/usePlanningTasks';
import { useConflicts } from './hooks/useConflicts';
import { PlanningGrid } from './components/PlanningGrid';
import { OccupancyStrip } from './components/OccupancyStrip';
import { PlanningLegend } from './components/PlanningLegend';
import { ConflictBanner } from './components/ConflictBanner';
import { ConflictPanel, type ConflictItemRef } from './components/ConflictPanel';
import { getMondayOfWeek, getWorkweekDates, addWeeks, toDateString, formatWeekRange } from '../werkrooster/utils/dateUtils';

export function PlanbordPage() {
  const { hasRole, isTeamManagerOrHR } = useAuth();
  const { showToast } = useToast();
  const isHrOrAdmin = hasRole(['hr', 'admin']);
  const canManageTasks = isTeamManagerOrHR;

  const [weekStart, setWeekStart] = useState<Date>(() => getMondayOfWeek(new Date()));
  const [departmentId, setDepartmentId] = useState<number | undefined>(undefined);
  const [isConflictPanelOpen, setIsConflictPanelOpen] = useState(false);
  const [highlightedCellKey, setHighlightedCellKey] = useState<string | null>(null);

  const dates = getWorkweekDates(weekStart).map(toDateString);
  const van = dates[0]!;
  const tot = dates[dates.length - 1]!;
  const todayIso = toDateString(new Date());

  const { data: departments } = useDepartments();
  const { data: board, isLoading, isError } = usePlanningBoard(van, tot, isHrOrAdmin ? departmentId : undefined);
  const { data: tasks } = usePlanningTasks(false);
  const { data: conflictsData } = useConflicts(van, tot, isHrOrAdmin ? departmentId : undefined);
  const upsertAssignment = useUpsertAssignment();
  const deleteAssignment = useDeleteAssignment();

  const conflicts = conflictsData?.conflicts ?? [];
  const qualificationWarnings = conflictsData?.qualificationWarnings ?? [];
  const conflictsCount = conflicts.length + qualificationWarnings.length;

  const goPrev = () => setWeekStart(addWeeks(weekStart, -1));
  const goNext = () => setWeekStart(addWeeks(weekStart, 1));
  const goToday = () => setWeekStart(getMondayOfWeek(new Date()));

  // Story C-4.3 (AC 2): scrollt naar + highlight de cel na "Aanpassen in
  // planbord" — een korte, zichtbare markering (2,5s), geen permanente state.
  useEffect(() => {
    if (!highlightedCellKey) return undefined;
    const el = document.querySelector(`[data-testid="planning-cell-${highlightedCellKey}"]`);
    // jsdom (test-omgeving) kent `scrollIntoView` niet — defensieve check i.p.v.
    // een polyfill, zelfde soort guard als elders in deze codebase.
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const timeout = setTimeout(() => setHighlightedCellKey(null), 2500);
    return () => clearTimeout(timeout);
  }, [highlightedCellKey, board]);

  async function handleRemoveConflict(item: ConflictItemRef) {
    try {
      await deleteAssignment.mutateAsync({ employeeId: item.employeeId, date: item.date, dayPart: item.dayPart });
      showToast('Indeling verwijderd', 'success', {
        duration: 8000,
        action: {
          label: 'Ongedaan maken',
          onClick: () => {
            upsertAssignment.mutate([{ employeeId: item.employeeId, date: item.date, dayPart: item.dayPart, taskId: item.taskId }]);
          },
        },
      });
    } catch {
      showToast('Indeling verwijderen mislukt — probeer opnieuw.', 'error');
    }
  }

  function handleAdjustConflict(item: ConflictItemRef) {
    const cellMonday = getMondayOfWeek(new Date(`${item.date}T00:00:00`));
    if (toDateString(cellMonday) !== toDateString(weekStart)) {
      setWeekStart(cellMonday);
    }
    setHighlightedCellKey(`${item.employeeId}-${item.date}`);
  }

  return (
    <Layout>
      <div data-testid="planbord-page" className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1E293B]">Planbord</h1>
          <p className="mt-1 text-sm text-gray-500">Deel teamleden per dagdeel in op een taak.</p>
        </div>

        <div className="bg-white rounded-lg border border-[#E2E8F0]">
          <ConflictBanner count={conflictsCount} onOpenPanel={() => setIsConflictPanelOpen(true)} />

          <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button type="button" onClick={goPrev} aria-label="Vorige week" className="px-2.5 py-1.5 text-gray-600 hover:bg-gray-100">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button type="button" onClick={goToday} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 border-x border-gray-200">
                  Vandaag
                </button>
                <button type="button" onClick={goNext} aria-label="Volgende week" className="px-2.5 py-1.5 text-gray-600 hover:bg-gray-100">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <span className="text-sm font-medium text-gray-900">{formatWeekRange(weekStart)}</span>
            </div>

            {isHrOrAdmin && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Afdeling:</span>
                <select
                  data-testid="planbord-afdeling-selector"
                  value={departmentId ?? ''}
                  onChange={(e) => setDepartmentId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
                >
                  <option value="">Alle</option>
                  {(departments ?? []).map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {isLoading ? (
            <div data-testid="planbord-skeleton" className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5A7A]" />
              <span className="ml-3 text-sm text-gray-500">Planbord laden...</span>
            </div>
          ) : isError || !board ? (
            <div className="text-center py-8 text-sm text-gray-500">Kon het planbord niet laden.</div>
          ) : (
            <>
              <PlanningGrid
                employees={board.employees}
                dates={dates}
                todayIso={todayIso}
                tasks={tasks ?? []}
                canManageTasks={canManageTasks}
                highlightedCellKey={highlightedCellKey}
              />
              {board.employees.length > 0 && (
                <>
                  <OccupancyStrip days={board.days} />
                  <PlanningLegend />
                </>
              )}
            </>
          )}
        </div>
      </div>

      <ConflictPanel
        isOpen={isConflictPanelOpen}
        onClose={() => setIsConflictPanelOpen(false)}
        conflicts={conflicts}
        qualificationWarnings={qualificationWarnings}
        onRemove={handleRemoveConflict}
        onAdjust={handleAdjustConflict}
      />
    </Layout>
  );
}
